import type { MembershipRole } from "@/lib/supabase/types";

/**
 * Sprint 6b — Role-Based Access Control.
 *
 * Single source of truth for what each membership role can see / do.
 * UI layers (Sidebar, ProtectedRoute, in-page buttons) call `canAccess`
 * / `can` against this matrix. RLS in Postgres enforces the same rules
 * at the data layer; this module exists to *hide* controls that would
 * otherwise appear and fail with "row violates policy" on click.
 *
 * Roles (from `diyafa_membership_role` enum):
 *   owner        — full control, billing + org admin
 *   manager      — full operations, no billing changes
 *   front_desk   — hotel ops: check in/out, reservations, guests
 *   rental_agent — rental ops: new rental, vehicle return, customers
 *   housekeeping — read rooms, mark room status; no financials
 *   accounting   — billing, reports, invoices; no ops actions
 */

export type Action =
  // Inventory writes (add/edit/delete rooms and vehicles, batch add)
  | "inventory:write"
  // Staff management (invite, revoke, change role)
  | "staff:manage"
  // Billing surfaces (charge, refund, plan change)
  | "billing:manage"
  // Reservation mutation (cancel, change dates)
  | "reservation:write"
  // Hotel ops (check in, check out, walk-in)
  | "hotel:ops"
  // Rental ops (new rental, vehicle return)
  | "rental:ops"
  // Housekeeping actions (mark room cleaned)
  | "housekeeping:ops"
  // Guest / customer profile writes
  | "guest:write";

const ACTION_MATRIX: Record<Action, MembershipRole[]> = {
  "inventory:write":  ["owner", "manager"],
  "staff:manage":     ["owner", "manager"],
  "billing:manage":   ["owner", "manager", "accounting"],
  "reservation:write":["owner", "manager", "front_desk"],
  "hotel:ops":        ["owner", "manager", "front_desk"],
  "rental:ops":       ["owner", "manager", "rental_agent"],
  "housekeeping:ops": ["owner", "manager", "housekeeping", "front_desk"],
  "guest:write":      ["owner", "manager", "front_desk", "rental_agent"],
};

/**
 * Path-level access matrix. Keys are route path prefixes; value is the
 * list of roles allowed to see / navigate there. Missing → allow all
 * (the default for /dashboard itself, notifications, etc.).
 */
const PATH_MATRIX: Record<string, MembershipRole[]> = {
  "/dashboard/checkin":           ["owner", "manager", "front_desk"],
  "/dashboard/walkin":            ["owner", "manager", "front_desk"],
  "/dashboard/checkout":          ["owner", "manager", "front_desk"],
  "/dashboard/reservations":      ["owner", "manager", "front_desk"],
  "/dashboard/rooms":             ["owner", "manager", "front_desk", "housekeeping"],
  "/dashboard/housekeeping":      ["owner", "manager", "housekeeping", "front_desk"],
  "/dashboard/rasad":             ["owner", "manager", "front_desk"],

  "/dashboard/vehicles":          ["owner", "manager", "rental_agent"],
  "/dashboard/new-rental":        ["owner", "manager", "rental_agent"],
  "/dashboard/vehicle-return":    ["owner", "manager", "rental_agent"],
  "/dashboard/rental-reports":    ["owner", "manager", "accounting"],

  "/dashboard/guests":            ["owner", "manager", "front_desk", "rental_agent", "accounting"],
  "/dashboard/customers":         ["owner", "manager", "rental_agent", "front_desk", "accounting"],
  "/dashboard/billing":           ["owner", "manager", "accounting"],
  "/dashboard/reports":           ["owner", "manager", "accounting"],
  "/dashboard/staff":             ["owner", "manager"],
  "/dashboard/batch-sync":        ["owner", "manager"],
  "/dashboard/billing-settings":  ["owner", "manager"],
  "/dashboard/activity":          ["owner", "manager"],
};

/** True iff the role is allowed to perform the given action. */
export function can(role: MembershipRole | null | undefined, action: Action): boolean {
  if (!role) return false;
  return ACTION_MATRIX[action].includes(role);
}

/**
 * True iff the role is allowed to navigate to the given path. Matches
 * by prefix — so "/dashboard/rooms/rate-management" inherits the
 * "/dashboard/rooms" entry unless a more specific one is added.
 */
export function canAccess(role: MembershipRole | null | undefined, path: string): boolean {
  if (!role) return false;
  // Find the longest-prefix match in the matrix.
  const keys = Object.keys(PATH_MATRIX).sort((a, b) => b.length - a.length);
  const match = keys.find((k) => path === k || path.startsWith(k + "/"));
  if (!match) return true; // default: allow (e.g. /dashboard, /dashboard/notifications)
  return PATH_MATRIX[match].includes(role);
}

/** Friendly label used in "Access denied" empty states. */
export function roleLabel(role: MembershipRole, isAr: boolean): string {
  const map: Record<MembershipRole, [string, string]> = {
    owner:        ["Owner", "المالك"],
    manager:      ["Manager", "المدير"],
    front_desk:   ["Front Desk", "الاستقبال"],
    rental_agent: ["Rental Agent", "وكيل التأجير"],
    housekeeping: ["Housekeeping", "التدبير المنزلي"],
    accounting:   ["Accounting", "المحاسبة"],
  };
  const [en, ar] = map[role];
  return isAr ? ar : en;
}
